import * as Sentry from '@sentry/nextjs';

// A stale tab (or, per BC-2905's incident, a bad response cached by Cloudflare/the browser
// during a deploy's asset-sync window) can make the browser request a JS chunk that the server
// no longer has, surfacing as this specific error instead of the page just working. A single
// automatic reload almost always fixes it, since it fetches a fresh HTML shell pointing at the
// chunks that actually exist right now - so users should never see the generic "Ups!" screen for
// this specific, self-healing case. Reload only once per tab session: if it's still failing after
// that, it's a real problem worth surfacing (and worth Sentry capturing) instead of reload-looping.
const RELOAD_FLAG_KEY = 'bc-chunk-load-reload-attempted';
// Written by the inline script in app/layout.tsx (see chunkLoadRecoveryScript) when a static
// <script> fails to load; reported to Sentry on the next page load by reportPendingScriptLoadFailure.
const SCRIPT_FAILURE_KEY = 'bc-script-load-failure';

// Inline (not a module) on purpose: a 403 on the app's own chunks leaves React unhydrated, so no
// bundled recovery code can be trusted to run. Failed <script> loads don't throw, so this listens
// for the capture-phase error event. Reloads at most once per 60s to avoid a reload loop.
export const chunkLoadRecoveryScript = `(function(){window.addEventListener('error',function(e){try{var t=e.target;if(!t||t.tagName!=='SCRIPT'||!t.src||t.src.indexOf('/_next/static/')===-1)return;var k='bc-script-load-reload-at',last=+(sessionStorage.getItem(k)||0);if(Date.now()-last<60000)return;sessionStorage.setItem(k,String(Date.now()));sessionStorage.setItem('${SCRIPT_FAILURE_KEY}',t.src);location.reload();}catch(_){}},true);})();`;

export function reportPendingScriptLoadFailure(): void {
  try {
    const src = window.sessionStorage.getItem(SCRIPT_FAILURE_KEY);
    if (!src) return;
    window.sessionStorage.removeItem(SCRIPT_FAILURE_KEY);
    Sentry.captureMessage('Static script failed to load, page reloaded', { level: 'warning', extra: { src } });
  } catch {
    // sessionStorage can throw (private mode, blocked storage) - reporting is best-effort
  }
}

export function isChunkLoadError(error: (Error & { digest?: string }) | null | undefined): boolean {
  if (!error) return false;
  return (
    error.name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(error.message || '') ||
    /Failed to fetch dynamically imported module/i.test(error.message || '')
  );
}

/**
 * If `error` is a ChunkLoadError and this tab hasn't already tried reloading once this session,
 * reloads the page and returns true (caller should skip rendering its normal error UI/Sentry
 * capture, since a reload is already in flight). Returns false otherwise, meaning the caller
 * should fall through to its normal error handling.
 */
export function reloadOnceForChunkLoadError(error: (Error & { digest?: string }) | null | undefined): boolean {
  if (typeof window === 'undefined' || !isChunkLoadError(error)) return false;
  if (window.sessionStorage.getItem(RELOAD_FLAG_KEY) === '1') return false;
  window.sessionStorage.setItem(RELOAD_FLAG_KEY, '1');
  Sentry.captureMessage('ChunkLoadError, reloading page', { level: 'warning', extra: { message: error?.message } });
  window.location.reload();
  return true;
}
